import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import Pill from '../components/Pill';
import { fmt } from '../utils/format';
import { printInvoices } from '../utils/printInvoices';

const ORDER_LIMIT = 100;

const trunc = (str, len) => {
  if (!str) return '—';
  return str.length > len ? str.slice(0, len) + '…' : str;
};

const stageColor = (stage) => {
  if (!stage) return 'muted';
  const s = stage.toLowerCase();
  if (s.includes('fully picked'))      return 'green';
  if (s.includes('new'))               return 'blue';
  if (s.includes('awaiting'))          return 'gold';
  if (s.includes('warehouse'))         return 'blue';
  return 'muted';
};

// ── OpenOrdersList Page ───────────────────────────────────────────────────────
// The newest ORDER_LIMIT undispatched (non-VOID) sales orders, any stage, one
// row per line. The sales order filter runs server-side across all open
// orders, so older ones can still be found. Selection and printing are per
// sales order, same invoice print as Smart Buyer Dispatch.
const OpenOrdersList = () => {
  const navigate = useNavigate();
  const [params, setParams]     = useSearchParams();
  const soRef                   = params.get('so') || '';
  const [input, setInput]       = useState(soRef);
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [selected, setSelected] = useState(new Set());
  const [printing, setPrinting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSelected(new Set());
    const { data, error: err } = await supabase.rpc('get_open_orders_list', {
      p_limit: ORDER_LIMIT, p_order_ref: soRef || null,
    });
    if (err) setError(err.message);
    setRows(data || []);
    setLoading(false);
  }, [soRef]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    const v = input.trim().toUpperCase();
    setInput(v);
    setParams(v ? { so: v } : {});
  };

  const handleClear = () => {
    setInput('');
    setParams({});
  };

  const orderRefs = useMemo(() => [...new Set(rows.map(r => r.order_ref))], [rows]);
  const allSelected  = orderRefs.length > 0 && orderRefs.every(r => selected.has(r));
  const someSelected = orderRefs.some(r => selected.has(r));

  const handleToggle = (orderRef) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(orderRef)) next.delete(orderRef); else next.add(orderRef);
      return next;
    });
  };

  const handleToggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(orderRefs));
  };

  const handlePrint = async () => {
    if (!selected.size) return;
    setPrinting(true);
    await printInvoices([...selected]);
    setPrinting(false);
  };

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/dispatch-tools')}>← Dispatch Tools</button>
        <span className="page-nav__title">Open Orders</span>
      </div>

      <section className="section">
        <SectionHeader
          title={soRef ? `Open Orders matching ${soRef}` : `Latest ${ORDER_LIMIT} Open Orders`}
          count={orderRefs.length}
          onRefresh={load}
          loading={loading}
          actions={
            <>
              <form onSubmit={handleSearch} style={{ display: 'inline-flex', gap: 6, marginRight: 8 }}>
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Sales order e.g. HC99783"
                  style={{
                    background: 'var(--bg3)', color: 'var(--white)',
                    border: '1px solid var(--border2)', borderRadius: 4,
                    padding: '4px 10px', fontFamily: 'var(--font-body)', fontSize: 12, width: 170,
                  }}
                />
                <button type="submit" className="action-btn action-btn--reset">Filter</button>
                {soRef && <button type="button" className="action-btn action-btn--ghost" onClick={handleClear}>Clear</button>}
              </form>
              <button
                className="action-btn action-btn--close"
                onClick={handlePrint}
                disabled={printing || selected.size === 0}
                style={{ marginRight: 8, opacity: selected.size === 0 ? 0.4 : 1 }}
              >
                {printing ? '…' : `🖨 Print${selected.size > 0 ? ` ${selected.size} Invoice${selected.size !== 1 ? 's' : ''}` : ''}`}
              </button>
            </>
          }
        />
        <p className="section__desc">
          The newest {ORDER_LIMIT} undispatched sales orders (VOID excluded, any stage), newest first.
          Filtering by sales order searches all open orders, not just these. One row per order line;
          ticking a row selects the whole sales order for printing. Stock On Hand is red when it
          doesn't cover the line's unmoved qty.
          {selected.size > 0 && <strong style={{ color: 'var(--green-light)', marginLeft: 8 }}>{selected.size} selected</strong>}
        </p>

        {error && <div className="empty-row" style={{ color: 'var(--red)' }}>{error}</div>}
        {loading ? (
          <div className="loading-row">Loading...</div>
        ) : !rows.length ? (
          <div className="empty-row">{soRef ? `No open orders matching ${soRef}` : 'No open orders'}</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Sales Order</th>
                  <th style={{ width: 36, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                      onChange={handleToggleAll}
                      title="Select all"
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th>Item Code</th>
                  <th>Item Name</th>
                  <th>Stage</th>
                  <th>Customer</th>
                  <th>Created</th>
                  <th className="num">Ordered</th>
                  <th className="num">Moved</th>
                  <th className="num">Stock On Hand</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const isSelected = selected.has(r.order_ref);
                  const short = Number(r.stock_on_hand || 0) < Number(r.item_qty) - Number(r.item_qty_moved);
                  return (
                    <tr
                      key={`${r.order_ref}-${r.item_code}-${i}`}
                      className={`data-row ${isSelected ? 'data-row--selected' : ''}`}
                      onClick={() => handleToggle(r.order_ref)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td><span className="code-badge">{r.order_ref}</span></td>
                      <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggle(r.order_ref)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td><span className="code-badge code-badge--sm">{r.item_code}</span></td>
                      <td className="product-name">{trunc(r.item_name, 40)}</td>
                      <td><Pill label={r.stage || '—'} color={stageColor(r.stage)} /></td>
                      <td>{trunc(r.customer_name, 20)}</td>
                      <td className="muted">{r.created_date ? new Date(r.created_date).toLocaleDateString('en-NZ') : '—'}</td>
                      <td className="num">{fmt(r.item_qty)}</td>
                      <td className="num">{fmt(r.item_qty_moved)}</td>
                      <td className={`num ${short ? 'neg' : ''}`}>{r.stock_on_hand == null ? '—' : fmt(r.stock_on_hand)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

export default OpenOrdersList;
